/**
 * A lista de clientes, paginada por cursor (D24, H17).
 *
 * ── Por que o mesmo desenho da timeline, e não `useInfiniteQuery` ─────────
 *
 * O produto já tem uma rolagem contínua — a da timeline do cliente — e ela
 * acumula páginas à mão pelo mesmo motivo que aqui: o cursor é assinado e
 * opaco, e cada página só existe a partir da anterior. Duas rolagens com
 * desenhos diferentes na mesma base de código é o começo de duas correções
 * diferentes para o mesmo defeito.
 *
 * ── A busca é DEBOUNCED, e o recorte NÃO ──────────────────────────────────
 *
 * Cada tecla numa busca que varre nome, e-mail, telefone e documento é uma
 * consulta com `ILIKE` e `EXISTS` no banco. "Marcos" digitado devagar são seis
 * consultas, das quais cinco já não interessam quando a sexta volta.
 *
 * Trezentos milissegundos é o intervalo em que a pessoa ainda percebe a lista
 * respondendo ao que ela digitou, e não a uma tecla anterior.
 *
 * Recorte e ordem são CLIQUE, e clique não se repete trinta vezes por segundo:
 * atrasá-los faria a pílula acender e a lista ficar parada por um terço de
 * segundo, que é o tempo suficiente para alguém clicar de novo.
 *
 * ── A resposta fora de ordem é descartada ─────────────────────────────────
 *
 * "Mar" pode voltar DEPOIS de "Marcos" — rede não garante ordem. Sem a marca
 * de qual busca cada resposta pertence, a lista mostraria o resultado de "Mar"
 * com "Marcos" escrito no campo, e a pessoa concluiria que a busca está
 * quebrada.
 *
 * ── As contagens sobrevivem à página seguinte ─────────────────────────────
 *
 * Elas vêm em toda resposta, e valem para a base inteira — não para a página.
 * Guardá-las junto dos itens faria as pílulas piscarem a cada rolagem; e
 * zerá-las durante o carregamento faria "Esfriando 12" virar "Esfriando 0" por
 * um instante, que é a pior leitura possível para quem confere onde há
 * trabalho.
 *
 * ── Cada recorte fica guardado (26/10/2026) ───────────────────────────────
 *
 * Trocar de aba ("Todos", "Sem responsável"…) recomeçava do zero: esqueleto e
 * espera a cada clique. Agora a primeira página de cada pedido (recorte, ordem,
 * termo e campos) fica no cache do React Query: ao voltar, ela aparece na hora
 * e a lista se atualiza por baixo, sem piscar. Só a primeira página: as outras
 * dependem do cursor, que vale para aquela leitura.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ClienteDaLista,
  ContagensDeClientes,
  OrdemDeClientes,
  PaginaDeClientes,
  RecorteDeClientes,
} from '@cliqcrm/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { chaves } from '@/shared/api/chaves';
import { useApi } from '@/shared/api/consultas';
import { useSessao } from '@/shared/sessao/contexto';

/** A primeira página guardada de um pedido. */
interface Retrato {
  readonly pagina: PaginaDeClientes;
  readonly termo: string;
}

/** O que a tela pede: o recorte, a ordem e o termo. */
export interface FiltroDaLista {
  readonly busca: string;
  readonly recorte: RecorteDeClientes;
  readonly ordenar: OrdemDeClientes;
  readonly direcao: 'asc' | 'desc';
  /** O filtro por campo personalizado (#568), no texto que a API lê. */
  readonly campos?: string;
}

export interface ListaDeClientes {
  readonly itens: readonly ClienteDaLista[];
  readonly total: number;
  readonly contagens: ContagensDeClientes | null;
  readonly frescor: PaginaDeClientes['frescor'] | null;
  readonly temMais: boolean;
  readonly carregando: boolean;
  /** `true` só na primeira página: a rolagem não deve piscar a tela inteira. */
  readonly carregandoPrimeira: boolean;
  readonly erro: unknown;
  /**
   * O termo a que os itens na tela respondem — não o que está digitado.
   *
   * Entre a tecla e a resposta há a espera da busca e a ida ao servidor; nesse
   * intervalo os itens ainda são da busca anterior. Quem age sobre "o primeiro
   * da lista" (o Enter do compartilhar contato, #595) confere por aqui que a
   * lista já é a do termo digitado.
   */
  readonly termoDosItens: string | null;
  readonly carregarMais: () => void;
  readonly recarregar: () => void;
}

const ESPERA_DA_BUSCA_MS = 300;

export function useListaDeClientes(filtro: FiltroDaLista): ListaDeClientes {
  const api = useApi();
  const cache = useQueryClient();
  const { estado } = useSessao();
  // A organização e quem pede: a lista é recortada pela carteira da pessoa.
  const organizacao = estado.nome === 'autenticado' ? estado.me.tenant.id : '';
  const usuario = estado.nome === 'autenticado' ? estado.me.usuario.id : '';

  const [termo, definirTermo] = useState(filtro.busca);
  const [itens, definirItens] = useState<readonly ClienteDaLista[]>([]);
  const [cursor, definirCursor] = useState<string | null>(null);
  const [total, definirTotal] = useState(0);
  const [contagens, definirContagens] = useState<ContagensDeClientes | null>(null);
  const [frescor, definirFrescor] = useState<PaginaDeClientes['frescor'] | null>(null);
  const [temMais, definirTemMais] = useState(false);
  const [carregando, definirCarregando] = useState(false);
  const [erro, definirErro] = useState<unknown>(null);
  const [termoDosItens, definirTermoDosItens] = useState<string | null>(null);

  // O termo só vira consulta depois da pausa — ver o cabeçalho.
  useEffect(() => {
    const relogio = setTimeout(() => definirTermo(filtro.busca), ESPERA_DA_BUSCA_MS);
    return () => clearTimeout(relogio);
  }, [filtro.busca]);

  /*
   * O recorte e a ordem entram já resolvidos, com o termo esperado.
   *
   * Um objeto novo a cada render reiniciaria a lista a cada render; o `useMemo`
   * sobre os quatro campos é o que faz "mudou de verdade" ser mudança de VALOR,
   * e não de identidade.
   */
  const pedido = useMemo(
    () => ({
      termo,
      recorte: filtro.recorte,
      ordenar: filtro.ordenar,
      direcao: filtro.direcao,
      campos: filtro.campos ?? '',
    }),
    [termo, filtro.recorte, filtro.ordenar, filtro.direcao, filtro.campos],
  );

  /** Qual pedido a resposta que chegar pertence. Ver o cabeçalho. */
  const vezAtual = useRef(0);
  const emCurso = useRef(false);

  const buscar = useCallback(
    (cursorAtual: string | null, atual: typeof pedido) => {
      if (!api) return;
      /*
       * A trava vale só para a PRÓXIMA PÁGINA, que depende do cursor da
       * anterior. Pedido novo (outra busca, outro recorte) sempre sai: barrá-lo
       * por haver outro no ar descartava a busca nova em silêncio, e a lista
       * ficava com o resultado da anterior e o termo novo escrito no campo. A
       * resposta que chegar fora de ordem já é descartada por `vezAtual`.
       */
      if (cursorAtual !== null && emCurso.current) return;

      emCurso.current = true;
      const minhaVez = cursorAtual === null ? vezAtual.current + 1 : vezAtual.current;
      vezAtual.current = minhaVez;

      definirCarregando(true);
      definirErro(null);

      void api
        .listarClientes({
          consulta: {
            ...(atual.termo.trim() !== '' ? { busca: atual.termo.trim() } : {}),
            recorte: atual.recorte,
            ordenar: atual.ordenar,
            direcao: atual.direcao,
            ...(atual.campos !== '' ? { campos: atual.campos } : {}),
            ...(cursorAtual ? { cursor: cursorAtual } : {}),
          },
        })
        .then((pagina) => {
          // Chegou tarde: outro pedido já começou. Aplicar isto trocaria a
          // lista debaixo do que a pessoa está lendo.
          if (vezAtual.current !== minhaVez) return;

          definirItens((anteriores) =>
            cursorAtual === null ? pagina.itens : [...anteriores, ...pagina.itens],
          );
          if (cursorAtual === null) {
            definirTermoDosItens(atual.termo.trim());
            cache.setQueryData<Retrato>(chaves.listaDeClientes(organizacao, usuario, atual), {
              pagina,
              termo: atual.termo.trim(),
            });
          }
          definirCursor(pagina.cursor_proximo);
          definirTemMais(pagina.tem_mais);
          definirTotal(pagina.total_no_escopo);
          definirContagens(pagina.contagens);
          definirFrescor(pagina.frescor);
        })
        .catch((falha: unknown) => {
          if (vezAtual.current === minhaVez) definirErro(falha);
        })
        .finally(() => {
          // Só o pedido da vez solta a trava: um anterior que termina depois
          // não pode liberar a próxima página enquanto o atual ainda está no ar.
          if (vezAtual.current === minhaVez) {
            emCurso.current = false;
            definirCarregando(false);
          }
        });
    },
    [api, cache, organizacao, usuario],
  );

  // Pedido novo recomeça do zero: são outras páginas, e o cursor da anterior
  // não vale — a API recusa cursor emitido para outra ordenação.
  useEffect(() => {
    // O recorte já visto aparece na hora; a busca abaixo o atualiza por baixo.
    const guardado = cache.getQueryData<Retrato>(
      chaves.listaDeClientes(organizacao, usuario, pedido),
    );
    if (guardado) {
      definirItens(guardado.pagina.itens);
      definirTermoDosItens(guardado.termo);
      definirCursor(guardado.pagina.cursor_proximo);
      definirTemMais(guardado.pagina.tem_mais);
      definirTotal(guardado.pagina.total_no_escopo);
      definirContagens(guardado.pagina.contagens);
    } else {
      definirItens([]);
      definirTermoDosItens(null);
      definirCursor(null);
      definirTemMais(false);
    }
    buscar(null, pedido);
    // `buscar` é estável por `useCallback`; incluí-lo aqui é o certo.
  }, [pedido, buscar, cache, organizacao, usuario]);

  return {
    itens,
    total,
    contagens,
    frescor,
    temMais,
    carregando,
    carregandoPrimeira: carregando && itens.length === 0,
    erro,
    termoDosItens,
    carregarMais: useCallback(() => {
      if (temMais && cursor) buscar(cursor, pedido);
    }, [temMais, cursor, buscar, pedido]),
    recarregar: useCallback(() => buscar(null, pedido), [buscar, pedido]),
  };
}
